import React, { useState } from "react";
import { Copy, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import {
  formatContentForCopy,
  copyToClipboard,
  getFormatDisplayName,
  getFormatDescription,
  CopyFormat,
} from "@/utils/copyFormatters";

interface CopyContentButtonProps {
  content: any;
  title?: string;
  variant?: "default" | "outline" | "ghost" | "minimal";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showLabel?: boolean;
}

export function CopyContentButton({
  content,
  title,
  variant = "outline",
  size = "sm",
  className = "",
  showLabel = true,
}: CopyContentButtonProps) {
  const [copiedFormat, setCopiedFormat] = useState<CopyFormat | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const formats: CopyFormat[] = ["plain", "rich", "markdown"];

  const handleCopy = async (format: CopyFormat) => {
    try {
      const formattedContent = formatContentForCopy({
        format,
        content,
        title,
      });

      const success = await copyToClipboard(formattedContent, format);

      if (success) {
        setCopiedFormat(format);
        setIsOpen(false);

        toast({
          title: "Copied!",
          description: `Content copied as ${getFormatDisplayName(format)}`,
          variant: "default",
        });

        // Reset the copied state after 2 seconds
        setTimeout(() => setCopiedFormat(null), 2000);
      } else {
        throw new Error("Copy operation failed");
      }
    } catch (error) {
      console.error("Copy failed:", error);
      toast({
        title: "Copy Failed",
        description: "Unable to copy content to clipboard",
        variant: "destructive",
      });
    }
  };

  const getButtonIcon = () => {
    if (copiedFormat) {
      return <Check className="h-4 w-4 text-green-600" />;
    }
    return <Copy className="h-4 w-4" />;
  };

  const getButtonLabel = () => {
    if (copiedFormat) {
      return `Copied as ${getFormatDisplayName(copiedFormat)}`;
    }
    return showLabel ? "Copy" : "";
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`gap-2 ${className}`}
          disabled={copiedFormat !== null}
        >
          {getButtonIcon()}
          {showLabel && <span className="text-sm">{getButtonLabel()}</span>}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        {formats.map((format) => (
          <DropdownMenuItem
            key={format}
            onClick={() => handleCopy(format)}
            className="flex flex-col items-start py-3 cursor-pointer"
          >
            <div className="flex items-center gap-2 w-full">
              <Copy className="h-4 w-4" />
              <span className="font-medium">
                {getFormatDisplayName(format)}
              </span>
            </div>
            <span className="text-xs text-muted-foreground mt-1 ml-6">
              {getFormatDescription(format)}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Minimal version for inline use
interface CopyIconButtonProps {
  content: any;
  title?: string;
  className?: string;
}

export function CopyIconButton({
  content,
  title,
  className = "",
}: CopyIconButtonProps) {
  return (
    <CopyContentButton
      content={content}
      title={title}
      variant="ghost"
      size="icon"
      className={`h-6 w-8 opacity-60 hover:opacity-100 ${className}`}
      showLabel={false}
    />
  );
}
